import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import {
  mapPipelineBuildSummaryDto,
  mapPipelineDetailDto,
  mapPipelineListItemDto,
  mapQueryResult,
} from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mapMetricSorts } from '@/shared/data/metricSortFields';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import type { GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  PipelineDetailDto,
  PipelineBuildSummaryDto,
  PipelineListItemDto,
  PipelineProvider,
  PipelineSummaryDto,
  QueryFilter,
  QueryResult,
  QuerySort,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

const normalizeNullableString = (value?: string | null): string | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const queryPipelines = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsStartDay?: string;
    metricsEndDay?: string;
    previousMetricsStartDay?: string;
    previousMetricsEndDay?: string;
    include?: PipelineInclude;
  },
  tokenOverride?: string | null,
): Promise<QueryResult<PipelineListItemDto>> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts
    ? mapMetricSorts(input.sorts)
    : [{ field: 'createdAt', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<PipelineListItemDto>>(
      'pipelines/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        previousMetricsStartDay: input?.previousMetricsStartDay,
        previousMetricsEndDay: input?.previousMetricsEndDay,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapPipelineListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsStartDay?: string;
    metricsEndDay?: string;
  },
  tokenOverride?: string | null,
): Promise<PipelineSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<PipelineSummaryDto>(
      'pipelines/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryPipelineBuildSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsStartDay?: string;
    metricsEndDay?: string;
    latestOnly?: boolean;
  },
  tokenOverride?: string | null,
): Promise<PipelineBuildSummaryDto> => {
  const token = ensureToken(tokenOverride);
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    const result = await apiClient.post<PipelineBuildSummaryDto>(
      'pipelines/build-summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        latestOnly: input?.latestOnly ?? false,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapPipelineBuildSummaryDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getPipelineById = async (
  id: string,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<PipelineDetailDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<PipelineDetailDto>(`pipelines/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapPipelineDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getPipelineBySlug = async (
  slug: string | null,
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<PipelineDetailDto | null> => {
  if (!slug) {
    return null;
  }
  try {
    return await getPipelineById(slug, organisationId, tokenOverride);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) {
      throw err;
    }
    // Fall back to slug lookup when the id fetch fails.
  }

  const pageSize = 100;
  let page = 1;

  for (;;) {
    const result = await queryPipelines(
      null,
      organisationId,
      {
        page,
        pageSize,
        sorts: [{ field: 'createdAt', direction: 'desc' }],
        filters: [],
      },
      tokenOverride,
    );

    const match = result.items.find((item) => resolvePipelineSlug(item) === slug) ?? null;
    if (match) {
      return await getPipelineById(match.id, organisationId, tokenOverride);
    }

    if (result.items.length < pageSize || page * pageSize >= result.total) {
      return null;
    }

    page += 1;
  }
};

export const createPipeline = async (
  input: {
    projectId: string;
    provider: PipelineProvider;
    pipelineKey?: string | null;
    name: string;
    pipelineUrl?: string | null;
    defaultBranch?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<PipelineDetailDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    projectId: input.projectId.trim(),
    provider: input.provider,
    pipelineKey: normalizeNullableString(input.pipelineKey),
    name: input.name.trim(),
    pipelineUrl: normalizeNullableString(input.pipelineUrl),
    defaultBranch: normalizeNullableString(input.defaultBranch),
    tags: input.tags,
  };
  try {
    const result = await apiClient.post<PipelineDetailDto>('pipelines', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapPipelineDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updatePipeline = async (
  input: {
    id: string;
    provider?: PipelineProvider;
    pipelineKey?: string | null;
    name?: string | null;
    pipelineUrl?: string | null;
    defaultBranch?: string | null;
    tags?: string[];
  },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<PipelineDetailDto> => {
  const token = ensureToken(tokenOverride);
  const body = {
    provider: input.provider,
    pipelineKey: normalizeNullableString(input.pipelineKey),
    name: input.name === undefined ? undefined : normalizeNullableString(input.name),
    pipelineUrl: normalizeNullableString(input.pipelineUrl),
    defaultBranch: normalizeNullableString(input.defaultBranch),
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<PipelineDetailDto>(`pipelines/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapPipelineDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type PipelineInclude = {
  project?: boolean;
  latestBuild?: boolean;
  buildSummaries?: boolean;
  metrics?: boolean;
  metricDeltas?: boolean;
};

export const deletePipeline = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`pipelines/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const restorePipeline = async (
  input: { id: string },
  organisationId?: string | null,
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`pipelines/${input.id}/restore`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
